import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button";

import { WorkspacePicker } from "./WorkspacePicker";
import {
  fetchWorkspaceSession,
  WorkspaceSessionError,
} from "./session";

export function WorkspaceIndex(): ReactElement {
  const sessionQuery = useQuery({
    queryKey: ["workspace-session"],
    queryFn: ({ signal }) => fetchWorkspaceSession(signal),
    retry: false,
  });

  if (sessionQuery.isPending) return <WorkspaceIndexLoading />;
  if (
    sessionQuery.error instanceof WorkspaceSessionError &&
    sessionQuery.error.status === 401
  ) {
    return <Navigate to="/login" replace />;
  }
  if (sessionQuery.isError || !sessionQuery.data) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#f4f4f1] px-6 text-[#171816]">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">Couldn't load your projects</h1>
          <p className="mt-2 text-sm leading-6 text-[#74766f]">
            {sessionQuery.error?.message ?? "Please try again."}
          </p>
          <Button
            type="button"
            className="mt-5 bg-[#20272b] hover:bg-[#111719]"
            onClick={() => void sessionQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <WorkspacePicker workspaces={sessionQuery.data.workspaces} />
  );
}

function WorkspaceIndexLoading(): ReactElement {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[#f4f4f1] text-[#62716c]">
      <LoaderCircle className="size-5 animate-spin" aria-label="Loading projects" />
    </div>
  );
}
